import type { input } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { TextField } from '@mui/material';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { DialogType } from '../../../@types';
import { MarketGroup, MarketType } from '../../../@types/api';
import { DISPLAY_SELECT_OPTIONS, MARKET_TYPE_USED_FOR_OPTIONS, QUERY_KEYS } from '../../../constants';
import { MARKET_GROUP_TYPE, MARKET_TYPE_GROUP_DISPLAY_TYPE } from '../../../constants/enums';
import { useModal } from '../../../contexts/ModalContext';
import useMutateData from '../../../hooks/useMutateData';
import { useSports } from '../../../queries';
import { MarketMixFormData, marketMixSchema } from '../../../schema';
import { ensureArray } from '../../../utils';
import FormFieldStack from '../../atoms/FormFieldStack';
import Switch from '../../atoms/Switch';
import FormModalLayout from '../../layouts/FormModalLayout';
import FormSelect from '../../molecules/FormSelect';
import FormAutocomplete from '../FormAutocomplete';
import API_ROUTES from '../../../constants/apiRoutes';
import { getMarketTypeName } from '../../../helpers';

const DEFAULT_FORM_DATA: MarketMixFormData = {
  name: '',
  usedFor: '',
  displayType: MARKET_TYPE_GROUP_DISPLAY_TYPE.TOURNAMENT,
  marketTypeIds: [],
  isActive: false,
  sportId: '',
};

const MarketMixForm = ({ closeModal }: DialogType) => {
  const { item } = useModal<MarketGroup>();
  const { createData, updateData } = useMutateData('market-type-groups', [QUERY_KEYS.marketTypeGroups]);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
    setValue,
  } = useForm<input<typeof marketMixSchema>, unknown, MarketMixFormData>({
    defaultValues: DEFAULT_FORM_DATA,
    resolver: zodResolver(marketMixSchema),
  });

  const sportId = watch('sportId');
  const usedFor = watch('usedFor');
  const marketTypeIds = watch('marketTypeIds');

  const { data: sportsData } = useSports();

  useEffect(() => {
    if (item) {
      reset({
        ...item,
        marketTypeIds: item.marketTypes.map((market) => market.id),
      });
    }
  }, [item, reset]);

  useEffect(() => {
    const subscription = watch((_value, { name, type }) => {
      if (type !== 'change') return;

      if (name === 'sportId' || name === 'usedFor') {
        setValue('marketTypeIds', []);
      }
    });

    return () => subscription.unsubscribe();
  }, [watch, setValue]);

  const handleClose = () => {
    reset(DEFAULT_FORM_DATA);
    closeModal?.();
  };

  const onFormSubmit = (data: MarketMixFormData) => {
    if (item) {
      updateData(item.id, data, () => {
        handleClose();
      });
    } else {
      createData(
        {
          ...data,
          type: MARKET_GROUP_TYPE.MIXED,
        },
        () => {
          handleClose();
        }
      );
    }
  };

  return (
    <FormModalLayout
      onSave={handleSubmit(onFormSubmit)}
      label={item ? 'Edit market mix' : 'Add new market mix'}
      onClose={handleClose}
      isEdit={!!item}
      editEntity='market-type-groups'
    >
      <TextField error={!!errors.name} helperText={errors.name?.message} label='Name' {...register('name')} required />
      <FormSelect
        label='Sport'
        name='sportId'
        required
        control={control}
        error={errors.sportId}
        options={sportsData?.items || []}
        disabled={!!item}
      />
      <FormSelect
        label='Used for'
        name='usedFor'
        required
        control={control}
        error={errors.usedFor}
        options={MARKET_TYPE_USED_FOR_OPTIONS}
        disabled={!!item}
      />
      <FormSelect
        label='Display'
        name='displayType'
        required
        error={errors.displayType}
        control={control}
        options={DISPLAY_SELECT_OPTIONS}
        disabled
        closeOnSelect
      />
      <FormFieldStack label='Status'>
        <Controller name='isActive' control={control} render={({ field }) => <Switch {...field} ref={null} />} />
      </FormFieldStack>
      <FormAutocomplete<MarketType, MarketMixFormData>
        name='marketTypeIds'
        control={control}
        label='Markets'
        multiple
        url={API_ROUTES.MARKET_TYPES_TOURNAMENT_GROUPS_ELIGIBLE}
        queryKey={[QUERY_KEYS.marketTypes, sportId, usedFor, marketTypeIds]}
        queryParams={{
          sportId,
          usedFor: ensureArray(usedFor),
          marketTypeIds: ensureArray(marketTypeIds),
          marketTypeGroupType: MARKET_GROUP_TYPE.MIXED,
        }}
        disabled={!sportId || !usedFor}
        hookEnabled={!!sportId && !!usedFor}
        getOptionLabel={(options, value) => {
          const option = options.find((option) => option?.id === value);
          return option ? getMarketTypeName(option) : '';
        }}
        error={errors.marketTypeIds}
      />
    </FormModalLayout>
  );
};

export default MarketMixForm;
